<template>
    <div class="message get-location-message">
        <a-radio-group name="radioGroup" default-value="manual" :disabled="readonly" :value="method">
            <a-radio value="manual" @click="method = 'manual'">{{ $t("akinon.chooseAddress") }} </a-radio>
            <a-radio value="map" @click="method = 'map'">{{ $t("akinon.useMap") }}</a-radio>
        </a-radio-group>
        <a-divider />
        <div v-if="method === 'manual'">
            <div class="selects-wrapper" key="initial-location">
                <div class="item">
                    <span>{{ $t("akinon.country") }}</span>
                    <a-select
                        :value="address.selectedCountry"
                        :placeholder="$t('akinon.selectCountry')"
                        :filter-option="filterOption"
                        option-filter-prop="children"
                        show-search
                        @change="handleCountryChange"
                        :disabled="readonly"
                    >
                        <a-select-option v-for="country of countries" :key="country.iso2" :value="country.iso2">
                            {{ country.name }}
                        </a-select-option>
                    </a-select>
                </div>

                <div class="item">
                    <span>{{ $t("akinon.state") }}</span>
                    <a-select
                        :value="address.selectedState"
                        :placeholder="$t('akinon.selectState')"
                        :filter-option="filterOption"
                        option-filter-prop="children"
                        show-search
                        @change="handleStateChange"
                        :disabled="readonly"
                    >
                        <a-select-option v-for="state of states" :key="state.state_code" :value="state.state_code">
                            {{ state.name }}
                        </a-select-option>
                    </a-select>
                </div>
                <div class="item">
                    <span>{{ $t("akinon.city") }}</span>
                    <a-select
                        :value="address.selectedCity"
                        :placeholder="$t('akinon.selectCity')"
                        :filter-option="filterOption"
                        option-filter-prop="children"
                        show-search
                        @change="handleCityChange"
                        :disabled="readonly"
                    >
                        <a-select-option v-for="city of cities" :key="city.id" :value="city.id">
                            {{ city.name }}
                        </a-select-option>
                    </a-select>
                </div>
            </div>
        </div>
        <div v-else>
            <div id="location-share">
                <l-map ref="map" @ready="ready" :center="mapLocation" :zoom="zoom" class="map" @click="mapClick">
                    <l-tile-layer :url="url" />
                    <l-marker draggable :icon="icons.default" :lat-lng.sync="mapLocation" />
                </l-map>
                <div
                    class="btn mt-3 pointer"
                    @click="getCurrentLocation"
                    :class="{ disabled: !locationEnabled || readonly }"
                >
                    <md-icon>my_location</md-icon>
                    <span>{{ $t("message.useMyLocation") }}</span>
                    <div class="text-danger" v-if="!locationEnabled">({{ $t("message.locationPermsNeeded") }})</div>
                </div>
            </div>
        </div>
        <a-divider />
        <div class="buttons">
            <a-button type="primary" :disabled="readonly" @click="sendLocation">{{ $t("akinon.send") }}</a-button>
        </div>
    </div>
</template>

<script>
import L from "leaflet";
import log2 from "../../../../utils/log2";
import _ from "lodash";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { mapState } from "vuex";

const log = log2("GetLocationMessage");
export default {
    name: "GetLocationMessage",
    props: {
        message: {
            type: Object,
            required: true,
        },
        conversationId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showMap: false,
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            zoom: 12,
            icons: {
                default: L.icon({
                    iconUrl: "https://img.icons8.com/ios-glyphs/30/undefined/marker--v1.png",
                    iconSize: [34, 34],
                    iconAnchor: [17, 17],
                }),
            },
            method: "manual",
            address: {
                selectedCountry: _.get(this.message, "raw.payload.initialCountry", "TR"),
                selectedState: _.get(this.message, "raw.payload.initialState", "34"),
                selectedCity: _.get(this.message, "raw.payload.initialCity"),
            },

            countries: [],
            states: [],
            cities: [],
            location: {
                latitude: "41.008240",
                longitude: "28.978359",
            },
            mapLocation: {
                lat: "41.008240",
                lng: "28.978359",
            },
            readonly: false,
            locationEnabled: true,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => _.get(state.site.site, "_id"),
        }),
    },
    methods: {
        getCurrentLocation() {
            if (this.readonly) return;
            if (!!navigator.geolocation && !!navigator.geolocation.getCurrentPosition)
                navigator.geolocation.getCurrentPosition(this.userLocation.bind(this), () => {
                    this.locationEnabled = false;
                });
        },
        userLocation({ coords }) {
            log(coords);
            this.mapLocation = { lat: coords.latitude, lng: coords.longitude };
        },
        mapClick({ latlng, ...rest }) {
            log(latlng, rest);
            this.mapLocation = { lat: latlng.lat, lng: latlng.lng };
        },
        sendLocation() {
            log(
                "send location",
                this.location.latitude,
                this.location.longitude,
                `https://maps.google.com/?q=${this.location.latitude},${this.location.longitude}`
            );
            const command = this.conversationId.startsWith("test-")
                ? "chatbot/v2 test-user-location-selected"
                : "chatbot/v2 user-location-selected";
            sendSocketMessage(command, {
                siteId: this.siteId,
                conversationId: this.conversationId,
                location: this.location,
            });
            this.readonly = true;
        },
        calculateLocation() {
            const country = this.countries.find((c) => c.iso2 === this.address.selectedCountry);
            const state = this.states.find((s) => s.state_code === this.address.selectedState);
            const city = this.cities.find((c) => c.id === this.address.selectedCity);
            if (city) {
                this.location = {
                    ...this.location,
                    latitude: city.latitude,
                    longitude: city.longitude,
                };
            } else if (state) {
                this.location = {
                    ...this.location,
                    latitude: state.latitude,
                    longitude: state.longitude,
                };
            } else if (country) {
                this.location = {
                    ...this.location,
                    latitude: country.latitude,
                    longitude: country.longitude,
                };
            } else {
                log.warn("No location found", {
                    selectedCountry: this.address.selectedCountry,
                    selectedState: this.address.selectedState,
                    selectedCity: this.address.selectedCity,
                    country,
                    state,
                    city,
                    countries: this.countries,
                    states: this.states,
                    cities: this.cities,
                });
            }
            this.mapLocation = {
                lat: this.location.latitude,
                lng: this.location.longitude,
            };
            log("location ==>", this.location.latitude, this.location.longitude);
        },
        handleCountryChange(value) {
            log(`handleCountryChange ${value}`);
            this.address.selectedCountry = value;
            this.address.selectedState = null;
            this.address.selectedCity = null;
            this.states = [];
            this.getStates(value);
        },
        handleStateChange(value) {
            log(`handleStateChange ${value}`);
            this.address.selectedState = value;
            this.address.selectedCity = null;
            this.cities = [];
            this.getCities(this.address.selectedCountry, value);
        },
        handleCityChange(value) {
            log(`handleCityChange ${value}`);
            this.address.selectedCity = value;
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        getCountries() {
            log("getCountries");
            sendSocketMessage(
                "location get-countries",
                {
                    siteId: this.siteId,
                },
                (countries) => {
                    this.countries = countries;
                }
            );
        },
        getStates(country) {
            log("getStates", country);
            sendSocketMessage(
                "location find-states",
                {
                    siteId: this.siteId,
                    country,
                },
                (states) => {
                    this.states = states;
                }
            );
        },
        getCities(country, state) {
            log("getCities", { country, state });
            sendSocketMessage(
                "location find-cities",
                {
                    siteId: this.siteId,
                    country,
                    state,
                },
                (cities) => {
                    this.cities = cities;
                }
            );
        },
        listenSocket() {
            this.socketSub = socketMessageStream.subscribe((message) => {
                const { cmd, data } = message;
                if (cmd === "list_countries_response") {
                    const { countries } = data;
                    this.countries = countries;
                    this.calculateLocation();
                }
                if (cmd === "find_states_response") {
                    const { states } = data;
                    this.states = states;
                    this.calculateLocation();
                }
                if (cmd === "find_cities_response") {
                    const { cities } = data;
                    this.cities = cities;
                    this.calculateLocation();
                }
            });
        },
        ready() {
            if (!!this.$refs.map) {
                setTimeout(() => {
                    this.$refs.map.mapObject["invalidateSize"]();
                }, 300);
            }
        },
        openGoogleMaps() {
            window.open(
                `https://www.google.com/maps/search/?api=1&query=${this.location.latitude},${this.location.longitude}`,
                "_blank"
            );
        },
    },
    mounted() {
        log("mounted", this.message);
        this.getCountries();
        if (this.address.selectedCountry) this.getStates(this.address.selectedCountry);
        if (this.address.selectedCountry && this.address.selectedState)
            this.getCities(this.address.selectedCountry, this.address.selectedState);
        this.listenSocket();
        this.calculateLocation();
    },
    beforeDestroy() {
        this.socketSub.unsubscribe();
    },
    watch: {
        address: {
            handler() {
                log("address changed", this.address);
                this.calculateLocation();
            },
            deep: true,
        },
        mapLocation: {
            handler() {
                log("mapLocation changed", this.mapLocation);
                if (this.method === "map") {
                    if (!_.isEqual(this.location, this.mapLocation)) {
                        this.location = {
                            latitude: this.mapLocation.lat,
                            longitude: this.mapLocation.lng,
                        };
                    }
                }
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.get-location-message {
    display: flex;
    flex-direction: column;
    gap: 5px;
    border: 1px solid rgb(219, 225, 232);
    border-radius: 10px;
    overflow: hidden;
    width: 320px;
    background: rgb(255, 255, 255);
    padding: 10px;

    .radioGroup {
        display: flex;
        gap: 5px;
    }

    .map {
        height: 170px;
        width: 280px;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 0 3px #00000057;
    }

    .selects-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .item {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
    }

    .buttons {
        display: flex;
        justify-content: flex-end;
    }
}
</style>
